import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ROUTES } from "@/shared/constants";
import { RouteLoadingFallback, SuspenseOutlet } from "@/shared/components";
import UserLayout from "@/shared/components/layout/UserLayout";
import withAuthentication from "@/shared/hoc/withAuthentication";
import LimitedEditionVerificationPage from "@/modules/limited-edition/LimitedEditionVerificationPage";

const CollectionCardPublicPage = lazy(
  () => import("@/cards/collection-card/component/CollectionCardPublicPage")
);
const TaasCardsDeck = lazy(() => import("@/cards/common/TaasCardsDeck"));
const TalentRegistrationFormPage = lazy(
  () => import("@/modules/talent/register/pages/TalentRegistrationFormPage")
);
const ComingSoonPage = lazy(() => import("@/modules/sites/pages/ComingSoonPage"));
const SignInPage = lazy(() => import("@/modules/auth/pages/SignInPage"));
const LandingPage = lazy(() => import("@/modules/sites/pages/LandingPage"));
const UserSettingPage = lazy(() => import("@/modules/settings/pages/UserSettingPage"));
const RegisterOrganizationPage = lazy(
  () => import("@/modules/organization/pages/RegisterOrganizationPage")
);
const OrganizationHomePage = lazy(() =>
  import("@/modules/organization/pages/OrganizationHomePage").then((m) => ({
    default: m.OrganizationHomePage,
  }))
);
const OrganizationInvitesPage = lazy(() =>
  import("@/modules/organization/pages/OrganizationInvitesPage").then((m) => ({
    default: m.OrganizationInvitesPage,
  }))
);
const LobbyPage = lazy(() =>
  import("@/modules/tn/pages/LobbyPage").then((m) => ({ default: m.LobbyPage }))
);
const AvailableDeckCollectionPage = lazy(() =>
  import("@/modules/tn/pages").then((m) => ({ default: m.AvailableDeckCollectionPage }))
);
const TalentListPage = lazy(() =>
  import("@/modules/hall/pages/TalentListPage").then((m) => ({ default: m.default }))
);
const UserHallPage = lazy(() =>
  import("@/modules/hall/pages/UserHallPage").then((m) => ({ default: m.UserHallPage }))
);
const ShelfViewAllPage = lazy(() =>
  import("@/modules/hall/pages/ShelfViewAllPage").then((m) => ({ default: m.default }))
);
const MyFeedPage = lazy(() =>
  import("@/modules/user/pages/MyFeedPage").then((m) => ({ default: m.MyFeedPage }))
);
const CharmPage = lazy(() =>
  import("@/modules/charm/pages/CharmPage").then((m) => ({ default: m.CharmPage }))
);

const NotificationPage = lazy(() =>
  import("@/modules/notification/pages").then((m) => ({ default: m.NotificationPage }))
);
const InboxPage = lazy(() =>
  import("@/modules/inbox/pages").then((m) => ({ default: m.InboxPage }))
);
const UserWalletPage = lazy(() => import("@/modules/wallet/pages/UserWalletPage"));

const TalentDeckDetailPage = lazy(() =>
  import("@/modules/charm/pages/TalentDeckDetailPage").then((m) => ({
    default: m.TalentDeckDetailPage,
  }))
);
const PersonalDeckDetailPage = lazy(() =>
  import("@/modules/charm/pages/PersonalDeckDetailPage").then((m) => ({
    default: m.PersonalDeckDetailPage,
  }))
);
const AuthenticatedLayout = withAuthentication(UserLayout) as React.FC<{
  children?: React.ReactNode;
}>;

export function UserRoutes() {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route element={<SuspenseOutlet />}>
          <Route path={ROUTES.LANDING} element={<LandingPage />} />
          <Route path={ROUTES.HOME} element={<LandingPage />} />
          <Route path={ROUTES.COMING_SOON} element={<ComingSoonPage />} />
          <Route path={ROUTES.REGISTER_ORGANIZATION} element={<RegisterOrganizationPage />} />
          <Route path={ROUTES.ORGANIZATION_HOME} element={<OrganizationHomePage />} />
          <Route path={ROUTES.ORGANIZATION_INVITES} element={<OrganizationInvitesPage />} />
        </Route>

        <Route element={<AuthenticatedLayout />}>
          <Route element={<SuspenseOutlet />}>
            <Route path={ROUTES.LOBBY} element={<LobbyPage />} />
            <Route
              path={ROUTES.AVAILABLE_DECK_COLLECTIONS}
              element={<AvailableDeckCollectionPage />}
            />
            <Route path={ROUTES.LIMITED_EDITION} element={<LimitedEditionVerificationPage />} />
            <Route path={ROUTES.REGISTER_TALENT} element={<TalentRegistrationFormPage />} />
            <Route path={ROUTES.TALENTS} element={<TalentListPage />} />
            <Route path={ROUTES.USER_HALL} element={<UserHallPage />} />
            <Route path={ROUTES.TALENT_HALL_SHELF} element={<ShelfViewAllPage />} />
            <Route path={ROUTES.MY_FEED} element={<MyFeedPage />} />
            <Route path={ROUTES.CHARM} element={<CharmPage />} />
            <Route path={ROUTES.COLLECTION_CARD_PUBLIC} element={<CollectionCardPublicPage />} />
            <Route path={ROUTES.PERSONAL_CHARM_DETAIL} element={<PersonalDeckDetailPage />} />
            <Route path={ROUTES.CHARM_DETAIL} element={<TalentDeckDetailPage />} />
            <Route path={ROUTES.USER_HOME} element={<LobbyPage />} />
            <Route path={ROUTES.WALLET} element={<UserWalletPage />} />
            <Route path={ROUTES.SETTINGS} element={<UserSettingPage />} />
            <Route path={ROUTES.APP_CARD_DECK} element={<TaasCardsDeck />} />
            <Route path={ROUTES.NOTIFICATION} element={<NotificationPage />} />
            <Route path={ROUTES.INBOX} element={<InboxPage />} />
            <Route path="*" element={<Navigate to={ROUTES.LOBBY} replace />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
